/**
 * Passport References Section
 * Contact redaction when share_contact_info = false for non-owners.
 */

import { useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';
import { ReferenceResponses } from './ReferenceResponses';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Users, Plus, Pencil, Trash2, Lock, Loader2 } from 'lucide-react';
import {
  usePassportReferences, useCreateReference, useUpdateReference, useDeleteReference,
} from '@/hooks/usePassportSections';

interface Props {
  userId: string;
  isOwner: boolean;
  isReadOnly?: boolean;
}

interface FormData {
  name: string;
  relationship: string;
  company: string;
  phone: string;
  email: string;
  notes: string;
  share_contact_info: boolean;
}

const emptyForm: FormData = {
  name: '', relationship: '', company: '', phone: '', email: '', notes: '', share_contact_info: false,
};

export function PassportReferencesSection({ userId, isOwner, isReadOnly }: Props) {
  // FIX-R37-CRIT-2: pass isOwner so the hook narrows columns server-side
  // when the viewer isn't the owner. Phone/email no longer cross the wire
  // for non-owners; the UI redaction at line 119 becomes defense-in-depth.
  const { data: items, isLoading, isError } = usePassportReferences(userId, { isOwner });
  const createItem = useCreateReference();
  const updateItem = useUpdateReference();
  const deleteItem = useDeleteReference();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setForm({
      name: item.name, relationship: item.relationship || '', company: item.company || '',
      phone: item.phone || '', email: item.email || '', notes: item.notes || '',
      share_contact_info: item.share_contact_info ?? true,
    });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    const payload = {
      name: form.name.trim(),
      relationship: form.relationship.trim() || undefined,
      company: form.company.trim() || undefined,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      notes: form.notes.trim() || undefined,
      share_contact_info: form.share_contact_info,
    };
    if (editingId) {
      updateItem.mutate({ id: editingId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    } else {
      createItem.mutate({ user_id: userId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">References</CardTitle>
            {items && <Badge variant="secondary" className="text-xs">{items.length}</Badge>}
            {/* CORE-C GAP-002. Every entry here is the candidate's assertion
                that someone is a reference - never that referee's attestation
                about them - so the group is homogeneous and one badge beside
                the title qualifies all of it without bleeding onto anything
                of another origin. */}
            <PassportProvenanceBadge factGroupId="references" />
          </div>
          {isOwner && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* CORE-C GAP-003: a failed read is not a statement that the person
            has none. Checked BEFORE the empty branch, which is where the
            failure used to land. */}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <PassportSectionFallback sectionName="References" inline />
        ) : !items?.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {isOwner ? 'No references yet.' : 'No references to display.'}
          </p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="border rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{item.name}</p>
                    {!item.share_contact_info && isOwner && (
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                  {item.relationship && <p className="text-xs text-muted-foreground">{item.relationship}</p>}
                  {item.company && <p className="text-xs text-muted-foreground">{item.company}</p>}

                  {/* Contact info: redacted for non-owners when share_contact_info is false */}
                  {isOwner || item.share_contact_info ? (
                    <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                      {item.phone && <p>{item.phone}</p>}
                      {item.email && <p>{item.email}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic mt-1">Contact info available upon request</p>
                  )}
                  <ReferenceResponses referenceId={item.id} isOwner={isOwner && !isReadOnly} />
                </div>
                {isOwner && !isReadOnly && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteItem.mutate({ id: item.id, userId })}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Reference' : 'Add Reference'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Relationship</Label>
              <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="e.g. Former supervisor" />
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Share contact info with shops</Label>
                <p className="text-xs text-muted-foreground">If off, shops see "Available upon request"</p>
              </div>
              <Switch checked={form.share_contact_info} onCheckedChange={(c) => setForm({ ...form, share_contact_info: c })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name.trim() || createItem.isPending || updateItem.isPending}>
              {(createItem.isPending || updateItem.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
